import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import DetailPageLayout, { DetailSection } from '../utils/DetailPageLayout';
import AccidentListItem from './items/accidentListItem';
import AircraftListItem from './items/aircraftListItem';
import MissionsListItem from './items/missionsListItem';
import AirfieldListItem from './items/airfieldsListItem';
import DocumentsListItem from './items/documentsListItem';

function SingleGroupContent(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const url = `${process.env.REACT_APP_API_BASE_URL}/api/groups/${id}`;

  useEffect(() => {
    setIsLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((data) => setData(data))
      .catch((err) => setHasError(err))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <>
        <div className="detail-page">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  if (hasError || !data) {
    return (
      <>
        <div className="detail-page">
          <p>Could not load this group.</p>
        </div>
      </>
    );
  }

  const meta = [];
  if (data.AcAircraftType) meta.push({ label: 'Aircraft Type', value: data.AcAircraftType });
  if (data.AcLetter) meta.push({ label: 'Letter', value: data.AcLetter });
  if (data.WingLongName) meta.push({ label: 'Wing', value: data.WingLongName });
  if (data.AirDivLongName) meta.push({ label: 'Air Division', value: `${data.AirDivLongName}${data.AirDivSymbol ? ` (${data.AirDivSymbol})` : ''}` });

  const empty = (msg) => <p style={{ color: 'var(--color-ink-mute)', fontStyle: 'italic' }}>{msg}</p>;

  return (
    <>
      <DetailPageLayout eyebrow="Group" title={data.AcLongName || 'Group'} meta={meta}>
        {data.AcNotes && (
          <DetailSection title="Notes">
            <p>{data.AcNotes}</p>
          </DetailSection>
        )}

        {data.AcLongDescription && (
          <DetailSection title="History">
            <div dangerouslySetInnerHTML={{ __html: data.AcLongDescription }} />
          </DetailSection>
        )}

        <DetailSection title="Squadrons">
          {data.Squadrons && data.Squadrons.length > 0
            ? data.Squadrons.map((sd, i) => <p key={i}>{sd}</p>)
            : empty('No squadrons recorded for this group.')
          }
        </DetailSection>

        <DetailSection title="Records">
          <Tabs>
            <TabList>
              <Tab>Accidents ({data.Accidents ? data.Accidents.length : 0})</Tab>
              <Tab>Aircraft ({data.Aircrafts ? data.Aircrafts.length : 0})</Tab>
              <Tab>Airfields ({data.Airfields ? data.Airfields.length : 0})</Tab>
              <Tab>Missions ({data.Missions ? data.Missions.length : 0})</Tab>
              <Tab>Documents ({data.Documents ? data.Documents.length : 0})</Tab>
            </TabList>

            <TabPanel>
              {data.Accidents && data.Accidents.length > 0
                ? data.Accidents.map(acc => (
                    <AccidentListItem key={acc.Id} date={acc.Date} details={acc.Description} />
                  ))
                : empty('No accidents recorded for this group.')
              }
            </TabPanel>

            <TabPanel>
              {data.Aircrafts && data.Aircrafts.length > 0
                ? data.Aircrafts.map(ac => <AircraftListItem key={ac.Id} ac={ac} />)
                : empty('No aircraft recorded for this group.')
              }
            </TabPanel>

            <TabPanel>
              {data.Airfields && data.Airfields.length > 0
                ? data.Airfields.map(af => (
                    <AirfieldListItem key={af.Id} name={af.Name} arrival={af.Arrival} depart={af.Depart} />
                  ))
                : empty('No airfields recorded for this group.')
              }
            </TabPanel>

            <TabPanel>
              {data.Missions && data.Missions.length > 0
                ? data.Missions.map(ms => (
                    <MissionsListItem key={ms.Id} date={ms.Date} name={ms.Name} details={ms.Description} />
                  ))
                : empty('No missions recorded for this group.')
              }
            </TabPanel>

            <TabPanel>
              {data.Documents && data.Documents.length > 0
                ? data.Documents.map(doc => (
                    <DocumentsListItem key={doc.Id} docType={doc.DocType} subType={doc.SubType} details={doc.Description} />
                  ))
                : empty('No documents recorded for this group.')
              }
            </TabPanel>
          </Tabs>
        </DetailSection>
      </DetailPageLayout>
    </>
  );
}

export default SingleGroupContent;
